/**
 * useLeads — cached read of early-lifecycle contacts (leads) via the active CRM adapter.
 *
 * Mirrors `useDeals` in spirit but simpler: leads are a remarketing/prospecting
 * pool, not a paginated pipeline. We pull a single page of up to 100 and hold
 * it. Same 30s module-scope cache so navigating away and back stays warm.
 *
 * `refetch()` clears the cache key and re-fetches. No `loadMore` — a remarketing
 * pool past 100 leads wants its own dedicated surface, not this dashboard rail.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useCrm } from '../contexts/CrmContext';
import { AdapterError } from '../lib/crm/adapter';
import type { Contact } from '../lib/crm/types';

interface CacheEntry {
  leads: Contact[];
  ts: number;
}

const CACHE_TTL_MS = 30_000;
const LEADS_PAGE_SIZE = 100;
const cache = new Map<string, CacheEntry>();

interface UseLeadsReturn {
  leads: Contact[];
  loading: boolean;
  error: AdapterError | null;
  refetch: () => Promise<void>;
}

function cacheKey(crmType: string): string {
  return `${crmType}:leads:${LEADS_PAGE_SIZE}`;
}

export function useLeads(): UseLeadsReturn {
  const { adapter } = useCrm();
  const key = adapter ? cacheKey(adapter.type) : null;

  const [leads, setLeads] = useState<Contact[]>(() => {
    if (!key) return [];
    const hit = cache.get(key);
    if (hit && Date.now() - hit.ts < CACHE_TTL_MS) return hit.leads;
    return [];
  });
  const [loading, setLoading] = useState<boolean>(leads.length === 0);
  const [error, setError] = useState<AdapterError | null>(null);

  // Guard against concurrent fetches double-hitting the adapter.
  const inflight = useRef(false);

  const refetch = useCallback(async () => {
    if (!adapter || !key || inflight.current) return;
    inflight.current = true;
    setLoading(true);
    setError(null);
    try {
      const result = await adapter.listLeads({ limit: LEADS_PAGE_SIZE });
      setLeads(result.items);
      cache.set(key, { leads: result.items, ts: Date.now() });
    } catch (err) {
      const e =
        err instanceof AdapterError
          ? err
          : new AdapterError('UNKNOWN', err instanceof Error ? err.message : String(err));
      setError(e);
    } finally {
      setLoading(false);
      inflight.current = false;
    }
  }, [adapter, key]);

  // Auto-fetch on mount unless the cache is warm.
  useEffect(() => {
    if (!adapter || !key) return;
    const hit = cache.get(key);
    if (hit && Date.now() - hit.ts < CACHE_TTL_MS) {
      setLeads(hit.leads);
      setLoading(false);
      return;
    }
    void refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter, key]);

  return { leads, loading, error, refetch };
}

/** Test/storybook helper — clear the entire leads cache. */
export function clearLeadsCache() {
  cache.clear();
}
